// ── LinkedEntityDisplay — RA-FU-9 Linked Entity friendly-name display ─────────
//
// Replaces raw linked_entity_id UUID display on RiskAcceptanceDetailPage with
// a human-readable name resolved via the appropriate entity hook.
//
// Path B forward-ready: branches on linked_entity_type so that ORA.1+ can add
// an 'organization' branch without modifying RiskAcceptanceDetailPage itself.
//
// 'vendor'       → useVendor() lookup → vendor.name (happy path)
//                → truncated UUID with title tooltip (error fallback)
// 'organization' → useAuthStore tenant name (MULTI-OPTION-1 adjudication ORA.3b)
//                  org = current tenant; no separate lookup needed
// unknown type   → labeled fallback showing raw id in monospace
//
// D-RA-CLUSTER-INVARIANT-1: NO migration. UI-only change.

import { useVendor } from '@/api/hooks/useVendors';
import { useAuthStore } from '@/store/auth.store';

interface LinkedEntityDisplayProps {
  linkedEntityType: string | null | undefined;
  linkedEntityId: string | null | undefined;
}

export function LinkedEntityDisplay({
  linkedEntityType,
  linkedEntityId,
}: LinkedEntityDisplayProps) {
  if (!linkedEntityType || !linkedEntityId) return null;

  if (linkedEntityType === 'vendor') {
    return <VendorLinkedEntity vendorId={linkedEntityId} />;
  }

  if (linkedEntityType === 'organization') {
    return <OrganizationLinkedEntity />;
  }

  // Unknown / future entity type — labeled fallback
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">Linked Entity</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
        <span className="capitalize">{linkedEntityType}</span>:{' '}
        <span className="font-mono">{linkedEntityId}</span>
      </dd>
    </div>
  );
}

// ── OrganizationLinkedEntity ──────────────────────────────────────────────────
//
// ORA.3b: org name from auth context (MULTI-OPTION-1 adjudication).
// Org = current tenant; no separate API lookup needed.
// linked_entity_id not used — the org IS the current tenant.

function OrganizationLinkedEntity() {
  const { user } = useAuthStore();
  // Fallback hierarchy: user.tenantName → 'Organization'
  const orgName = user?.tenantName ?? 'Organization';
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">Linked Organization</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
        <span className="font-medium">{orgName}</span>
      </dd>
    </div>
  );
}

// ── VendorLinkedEntity ────────────────────────────────────────────────────────

function VendorLinkedEntity({ vendorId }: { vendorId: string }) {
  const { data: vendor, isLoading, isError } = useVendor(vendorId);

  if (isLoading) {
    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-text-secondary">Linked Vendor</dt>
        <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
          <em className="text-text-secondary">Loading…</em>
        </dd>
      </div>
    );
  }

  if (isError || !vendor) {
    // Error fallback: truncated UUID with full UUID in title tooltip
    const truncated = `${vendorId.slice(0, 8)}…${vendorId.slice(-4)}`;
    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-text-secondary">Linked Vendor</dt>
        <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
          <span title={vendorId} className="font-mono">
            {truncated}
          </span>
        </dd>
      </div>
    );
  }

  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">Linked Vendor</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
        <span className="font-medium">{vendor.name}</span>
      </dd>
    </div>
  );
}
